import { type FormEvent, useState } from 'react';
import { DESCRIPCION_ROL, NOMBRE_ROL, ROLES_LOTERO, type RolLotero } from '@plataforma/permisos';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { useSesion } from '@plataforma/web/sesion';
import { fechaCorta } from '../formato';

interface UsuarioLotero {
  id: string;
  correo: string;
  nombre: string;
  apellidos: string;
  roles: RolLotero[];
  activo: boolean;
  pendiente: boolean;
  creadoEn: string;
}

interface Props {
  /** Roles que se ofrecen (todos los del catálogo por omisión; el Portal solo dueño y operador). */
  roles?: readonly RolLotero[];
  rolInicial?: RolLotero;
  titulo?: string;
  subtitulo?: string;
  miga?: [string, string][];
}

/** Usuarios del negocio: el dueño (o gerente) invita por correo con su rol, cambia roles y desactiva. */
export function Usuarios({
  roles: ROLES = ROLES_LOTERO,
  rolInicial = 'vendedor',
  titulo = 'Usuarios',
  subtitulo = 'Quién entra a tu negocio y qué puede hacer cada uno',
  miga = [],
}: Props = {}) {
  const api = useApi();
  const { usuario: yo } = useSesion();
  const lista = useConsulta<UsuarioLotero[]>('/platform/mi-lotero/usuarios');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [rolesNuevo, setRolesNuevo] = useState<RolLotero[]>([rolInicial]);

  const accion = async (fn: () => Promise<unknown>, texto?: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      if (texto) setAviso(texto);
      lista.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const invitar = async (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    const forma = evento.currentTarget;
    const datos = new FormData(forma);
    if (!rolesNuevo.length) return setError('Elige al menos un rol');
    setEnviando(true);
    await accion(async () => {
      await api('POST', '/platform/mi-lotero/usuarios', {
        correo: datos.get('correo'),
        nombre: datos.get('nombre'),
        apellidos: datos.get('apellidos'),
        roles: rolesNuevo,
      });
      forma.reset();
      setRolesNuevo([rolInicial]);
    }, 'Invitación enviada: le llega un correo para elegir su contraseña o entrar con Google.');
    setEnviando(false);
  };

  const alternar = (u: UsuarioLotero, rol: RolLotero) => {
    const roles = u.roles.includes(rol) ? u.roles.filter((r) => r !== rol) : [...u.roles, rol];
    if (!roles.length) return setError('Un usuario necesita al menos un rol');
    void accion(() => api('PUT', `/platform/mi-lotero/usuarios/${u.id}/roles`, { roles }));
  };

  return (
    <>
      <div className="encabezado">
        <div>
          {miga.length > 0 && (
            <div className="subtitulo">
              {miga.map(([a, t]) => (
                <span key={a}>
                  <Link to={a}>{t}</Link> ›{' '}
                </span>
              ))}
            </div>
          )}
          <h1>{titulo}</h1>
          <div className="subtitulo">{subtitulo}</div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}

      <section className="seccion">
        <header>
          <h2>Equipo</h2>
        </header>
        {lista.error && <p className="error">{lista.error}</p>}
        <table>
          <thead>
            <tr>
              <th>Persona</th>
              {ROLES.map((rol) => (
                <th key={rol} title={DESCRIPCION_ROL[rol]}>
                  {NOMBRE_ROL[rol]}
                </th>
              ))}
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(lista.datos ?? []).map((u) => {
              const soyYo = u.id === yo?.id;
              return (
                <tr key={u.id} style={{ opacity: u.activo ? 1 : 0.55 }}>
                  <td>
                    {u.nombre} {u.apellidos} {soyYo && <small className="subtitulo">(tú)</small>}
                    <div className="vin">
                      {u.correo} · desde {fechaCorta(u.creadoEn)}
                    </div>
                  </td>
                  {ROLES.map((rol) => (
                    <td key={rol}>
                      <input
                        type="checkbox"
                        aria-label={`${NOMBRE_ROL[rol]} para ${u.correo}`}
                        checked={u.roles.includes(rol)}
                        disabled={!u.activo || (soyYo && rol === 'dueno')}
                        onChange={() => alternar(u, rol)}
                      />
                    </td>
                  ))}
                  <td>
                    {!u.activo ? (
                      <span className="etiqueta">desactivado</span>
                    ) : u.pendiente ? (
                      <span className="etiqueta aviso">invitación pendiente</span>
                    ) : (
                      <span className="etiqueta exito">activo</span>
                    )}
                  </td>
                  <td className="num">
                    {u.activo && u.pendiente && (
                      <button
                        className="boton"
                        onClick={() =>
                          void accion(
                            () => api('POST', `/platform/mi-lotero/usuarios/${u.id}/reenviar`),
                            `Invitación reenviada a ${u.correo}`,
                          )
                        }
                      >
                        Reenviar
                      </button>
                    )}{' '}
                    {!soyYo && (
                      <button
                        className="boton"
                        onClick={() =>
                          void accion(() =>
                            api(
                              'POST',
                              `/platform/mi-lotero/usuarios/${u.id}/${u.activo ? 'desactivar' : 'reactivar'}`,
                            ),
                          )
                        }
                      >
                        {u.activo ? 'Desactivar' : 'Reactivar'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="seccion">
        <header>
          <h2>Invitar a alguien</h2>
        </header>
        <form className="cuerpo formulario" onSubmit={(e) => void invitar(e)}>
          <label className="campo">
            <span>Correo</span>
            <input name="correo" type="email" required maxLength={120} />
          </label>
          <label className="campo">
            <span>Nombre</span>
            <input name="nombre" required maxLength={80} />
          </label>
          <label className="campo">
            <span>Apellidos</span>
            <input name="apellidos" required maxLength={80} />
          </label>
          <fieldset className="campo" style={{ border: 0, padding: 0 }}>
            <span>Roles</span>
            {ROLES.map((rol) => (
              <label key={rol} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <input
                  type="checkbox"
                  checked={rolesNuevo.includes(rol)}
                  onChange={() =>
                    setRolesNuevo((r) =>
                      r.includes(rol) ? r.filter((x) => x !== rol) : [...r, rol],
                    )
                  }
                />
                <strong>{NOMBRE_ROL[rol]}</strong>
                <small className="subtitulo">{DESCRIPCION_ROL[rol]}</small>
              </label>
            ))}
          </fieldset>
          <div>
            <button className="boton primario" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar invitación'}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
